import {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {SORT_KEYS} from '../sortKeys';
import useClickOutside from '../hooks/useClickOutside';
import {SortIcon} from './icons';

export default function SortMenu({sortKey, direction, onChange, keys}) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    const close = useCallback(() => setOpen(false), []);
    useClickOutside(open, close, rootRef);

    useEffect(() => {
        if (!open) return undefined;
        function onKey(e) {
            if (e.key === 'Escape') setOpen(false);
        }
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    const availableKeys = keys ?? Object.keys(SORT_KEYS);
    const entries = availableKeys.filter(k => SORT_KEYS[k]).map(k => [k, SORT_KEYS[k]]);

    const isManual = sortKey === 'manual';
    const currentLabel = SORT_KEYS[sortKey]?.label ?? SORT_KEYS.manual.label;

    function selectKey(nextKey) {
        onChange(nextKey, direction);
    }
    function selectDirection(nextDir) {
        if (isManual) return;
        onChange(sortKey, nextDir);
    }

    return (
        <div className="sort-menu" ref={rootRef}>
            <button
                type="button"
                className={`view-toggle-btn sort-menu-trigger${open ? ' active' : ''}${!isManual ? ' sort-menu-trigger--applied' : ''}`}
                onClick={() => setOpen(o => !o)}
                title={`Sort: ${currentLabel}`}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label="Sort"
            >
                <SortIcon />
            </button>
            {open && (
                <div className="sort-menu-popover" role="menu">
                    <div className="sort-menu-section-label">Sort by</div>
                    {entries.map(([key, {label}]) => (
                        <button
                            key={key}
                            type="button"
                            className={`sort-menu-option${sortKey === key ? ' sort-menu-option--active' : ''}`}
                            onClick={() => selectKey(key)}
                            role="menuitemradio"
                            aria-checked={sortKey === key}
                        >
                            {label}
                        </button>
                    ))}
                    <div className="sort-menu-divider" />
                    <div className="sort-menu-section-label">Direction</div>
                    <div
                        className={`sort-menu-direction${isManual ? ' sort-menu-direction--disabled' : ''}`}
                        role="group"
                        aria-label="Sort direction"
                    >
                        <button
                            type="button"
                            className={`sort-menu-direction-btn${direction === 'asc' ? ' sort-menu-direction-btn--active' : ''}`}
                            onClick={() => selectDirection('asc')}
                            disabled={isManual}
                            aria-pressed={direction === 'asc'}
                        >
                            Ascending
                        </button>
                        <button
                            type="button"
                            className={`sort-menu-direction-btn${direction === 'desc' ? ' sort-menu-direction-btn--active' : ''}`}
                            onClick={() => selectDirection('desc')}
                            disabled={isManual}
                            aria-pressed={direction === 'desc'}
                        >
                            Descending
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

SortMenu.propTypes = {
    sortKey: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
    onChange: PropTypes.func.isRequired,
    keys: PropTypes.arrayOf(PropTypes.string),
};
